import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';
import { Extension } from '@tiptap/core';
import { Bold, Italic, Underline, List, ListOrdered, Heading1, Heading2, Heading3, Link2 } from 'lucide-react';
import EmojiPickerButton from '@/components/shared/EmojiPickerButton';
import LinkExtension from '@tiptap/extension-link';
import MentionSuggestions from '@/components/shared/MentionSuggestions';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { normalizePastedSpaces } from '../../utils/sanitize';

// Toolbar button component
const ToolbarButton = ({ onClick, isActive, children, title, size = 'md' }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`flex items-center justify-center rounded transition-colors ${
      size === 'sm'
        ? 'w-7 h-7 sm:w-auto sm:h-auto sm:p-1'
        : 'w-8 h-8 sm:w-auto sm:h-auto sm:p-1.5'
    } ${
      isActive 
        ? 'bg-slate-200 dark:bg-[#3e4145] text-slate-900 dark:text-slate-100' 
        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-slate-100'
    }`}
  >
    {children}
  </button>
);

// Notion-style floating popover for inserting/editing links
const LinkPopover = ({ editor }) => {
  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState('');

  // Sync input value with current link href whenever popover opens
  React.useEffect(() => {
    if (open) {
      setValue(editor.getAttributes('link').href || '');
    }
  }, [open, editor]);

  const apply = () => {
    const url = value.trim();
    if (!url) {
      // Empty input -> remove existing link (if any)
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setOpen(false);
      return;
    }
    const href = /^(https?:\/\/|mailto:|tel:)/i.test(url) ? url : `https://${url}`;
    const { from, to } = editor.state.selection;
    const hasSelection = from !== to;
    const isInExistingLink = editor.isActive('link');
    if (!hasSelection && !isInExistingLink) {
      // Truly empty cursor: insert URL as linked text atomically
      editor.chain().focus()
        .insertContent({
          type: 'text',
          text: url,
          marks: [{ type: 'link', attrs: { href } }],
        })
        .run();
    } else {
      // Has selection OR cursor inside an existing link -> extend & (re)set
      editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
    }
    setOpen(false);
  };

  const remove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setOpen(false);
  };

  const isActive = editor.isActive('link');

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title="Insert Link"
          data-testid="rte-link-button"
          className={`flex items-center justify-center w-8 h-8 sm:w-auto sm:h-auto sm:p-1.5 rounded transition-colors ${
            isActive 
              ? 'bg-slate-200 dark:bg-[#3e4145] text-slate-900 dark:text-slate-100' 
              : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-slate-100'
          }`}
        >
          <Link2 className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        sideOffset={6}
        className="w-80 p-3"
      >
        <div className="space-y-2">
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onFocus={(e) => e.target.select()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                apply();
              }
            }}
            placeholder="Paste or type a URL"
            data-testid="rte-link-input"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 dark:border-white/10 rounded-md bg-slate-50 dark:bg-[#1e1f22] text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 focus:border-indigo-500"
          />
          <div className="flex items-center justify-end gap-2">
            {isActive && (
              <button
                type="button"
                onClick={remove}
                data-testid="rte-link-remove"
                className="text-xs px-2.5 py-1 rounded text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
              >
                Remove
              </button>
            )}
            <button
              type="button"
              onClick={apply}
              data-testid="rte-link-apply"
              className="text-xs px-3 py-1 rounded bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default function RichTextEditor({ 
  content = '', 
  onChange, 
  onSubmit,
  placeholder = 'Write something...',
  minHeight = '100px',
  className = '',
  minimal = false,
  members = null,
  groups = null,
  currentUserId = null,
  onMentionsChange = null,
  onMentionTrigger = null,
  // 104: Studio's composer puts bold / italic / list in the bottom tool row, so it
  // hides the built-in strip and drives the same commands through the instance.
  onEditorReady = null
}) {
  // Force re-render when editor state changes (for toolbar button highlights)
  const [, setForceUpdate] = React.useState(0);
  const [mentionQuery, setMentionQuery] = React.useState(null);
  const mentionsRef = React.useRef([]);
  const mentionSuggestionsRef = React.useRef(null);
  const mentionQueryRef = React.useRef(null);
  React.useEffect(() => { mentionQueryRef.current = mentionQuery; }, [mentionQuery]);
  
  // Use ref to store onSubmit so extension doesn't need to be recreated
  const onSubmitRef = React.useRef(onSubmit);
  React.useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  // Create a custom extension to handle CMD+Enter - only once
  const SubmitShortcut = React.useMemo(() => {
    return Extension.create({
      name: 'submitShortcut',
      addKeyboardShortcuts() {
        return {
          'Mod-Enter': () => {
            if (onSubmitRef.current) {
              onSubmitRef.current();
              return true;
            }
            return false;
          },
        };
      },
    });
  }, []);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        // StarterKit v3 bundles Underline and Link. We replace both with our
        // own configured versions below (custom inclusive() override on Link,
        // explicit Underline registration) — disable the bundled ones to avoid
        // "Duplicate extension names" TipTap warnings.
        link: false,
        underline: false,
      }),
      UnderlineExtension,
      LinkExtension
        .extend({
          // TipTap v3 hardcodes link `inclusive` to follow `autolink`.
          // We want autolink ON but the cursor to NOT extend the link when typing
          // at its right boundary, so override the getter directly.
          inclusive() {
            return false;
          },
          // R39: never carry a `target` on links inside the editor — not from
          // HTMLAttributes (new links) and not parsed from existing HTML (old
          // posts stored with target=_blank, pasted feed content). The desktop
          // shell reloads the window on internal target=_blank links; the
          // display layer (sanitizeHtml) re-adds target=_blank for viewing.
          addAttributes() {
            return {
              ...this.parent?.(),
              target: { default: null, parseHTML: () => null, renderHTML: () => ({}) },
            };
          },
        })
        .configure({
          autolink: true,
          openOnClick: false,
          protocols: ['http', 'https', 'mailto', 'tel'],
          // R39 (2026-09-07): no `target` on editor links. Displayed posts get
          // target=_blank from sanitizeHtml's DOMPurify hook instead. Inside
          // the editor a target=_blank link to a Worship OS URL made the
          // desktop shell reload the window (its "internal new-tab link →
          // navigate here" rule), wiping the draft being written.
          HTMLAttributes: {
            rel: 'noopener noreferrer',
          },
        }),
      SubmitShortcut,
    ],
    content,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      const isEmpty = html === '<p></p>' || html === '';
      onChange(isEmpty ? '' : html);

      // Detect @mention query from cursor position. Allow hyphens in tag names
      // (group tags can contain '-') without changing the lookbehind, so existing
      // trigger semantics are preserved.
      const { from } = editor.state.selection;
      const textBefore = editor.state.doc.textBetween(Math.max(0, from - 30), from, ' ');
      const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9-]*)$/);
      const newQuery = atMatch ? atMatch[2] : null;
      // Notify parent that an @-mention is in progress so it can lazy-load
      // members/groups data on transition from null -> not-null.
      if (newQuery !== null && mentionQueryRef.current === null && onMentionTrigger) {
        onMentionTrigger();
      }
      setMentionQuery(newQuery);
    },
    onSelectionUpdate: () => {
      // Force re-render to update toolbar button states
      setForceUpdate(n => n + 1);
    },
    onTransaction: () => {
      // Also update on any transaction (includes formatting changes)
      setForceUpdate(n => n + 1);
    },
    editorProps: {
      attributes: {
        class: 'outline-none',
        'data-placeholder': placeholder,
      },
      // R43: pasted rich text (Mail, Notes, Word) carries non-breaking spaces
      // that never wrap; normalise them so the post wraps at words.
      transformPastedHTML: (html) => normalizePastedSpaces(html),
      transformPastedText: (text) => normalizePastedSpaces(text),
      // R39: a click on a link while editing places the caret; it never follows
      // the link (every engine, including the desktop shell's WebKit).
      handleDOMEvents: {
        click: (view, event) => {
          const a = event.target?.closest?.('a[href]');
          if (a && view.dom.contains(a)) event.preventDefault();
          return false;
        },
      },
      handleKeyDown: (view, event) => {
        if (mentionQueryRef.current !== null && mentionSuggestionsRef.current?.handleKeyDown(event)) {
          return true;
        }
        return false;
      },
    },
  });

  // Update content when prop changes (for edit mode)
  React.useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content || '');
      // Clear tracked mentions when content is reset (e.g., after submitting a post)
      if (!content) {
        mentionsRef.current = [];
        setMentionQuery(null);
      }
    }
  }, [content, editor]);

  // Handle selecting a member or group from the mention dropdown
  const handleMentionSelect = React.useCallback((item) => {
    if (!editor) return;
    const { from } = editor.state.selection;
    const textBefore = editor.state.doc.textBetween(Math.max(0, from - 30), from, ' ');
    const atMatch = textBefore.match(/(^|[^a-zA-Z0-9])@([a-zA-Z0-9-]*)$/);
    if (!atMatch) return;
    const queryLen = atMatch[2].length + 1; // +1 for @

    if (item.kind === 'group') {
      const tag = item.tag;
      editor.chain().focus()
        .deleteRange({ from: from - queryLen, to: from })
        .insertContent(`@${tag} `)
        .run();
      mentionsRef.current = [
        ...mentionsRef.current.filter(m => m.group_id !== item.id),
        { group_id: item.id, tag, name: item.name },
      ];
    } else {
      const name = item.user_name;
      editor.chain().focus()
        .deleteRange({ from: from - queryLen, to: from })
        .insertContent(`@${name} `)
        .run();
      mentionsRef.current = [
        ...mentionsRef.current.filter(m => m.user_id !== item.user_id),
        { user_id: item.user_id, name },
      ];
    }
    if (onMentionsChange) onMentionsChange(mentionsRef.current);
    setMentionQuery(null);
  }, [editor, onMentionsChange]);

  React.useEffect(() => {
    if (editor && onEditorReady) onEditorReady(editor);
  }, [editor, onEditorReady]);

  if (!editor) return null;

  return (
    <div className={`relative z-10 border border-slate-200 dark:border-white/10 rounded-lg bg-slate-50 dark:bg-[#1e1f22] focus-within:bg-white dark:focus-within:bg-[#2d2f33] focus-within:border-slate-300 transition-colors ${className}`}>
      {/* Inner clip wrapper — preserves rounded-corner clipping for the toolbar/editor
          chrome while keeping the @-mention popover (rendered as a sibling below)
          free to overflow upward without being cropped. */}
      <div className="rounded-lg overflow-hidden">
        {/* Toolbar */}
        <div data-ui="rte-toolbar" className={`flex items-center border-b border-slate-200 dark:border-white/10 bg-card flex-wrap ${minimal ? 'gap-0.5 px-1 py-0.5' : 'gap-1 p-2'}`}>
        {!minimal && (
          <>
            <ToolbarButton 
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} 
              isActive={editor.isActive('heading', { level: 1 })} 
              title="Heading 1"
            >
              <Heading1 className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton 
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} 
              isActive={editor.isActive('heading', { level: 2 })} 
              title="Heading 2"
            >
              <Heading2 className="w-4 h-4" />
            </ToolbarButton>
            <ToolbarButton 
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} 
              isActive={editor.isActive('heading', { level: 3 })} 
              title="Heading 3"
            >
              <Heading3 className="w-4 h-4" />
            </ToolbarButton>
            <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-1" />
          </>
        )}
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleBold().run()} 
          isActive={editor.isActive('bold')} 
          title="Bold (Ctrl+B)"
          size={minimal ? 'sm' : 'md'}
        >
          <Bold className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleItalic().run()} 
          isActive={editor.isActive('italic')} 
          title="Italic (Ctrl+I)"
          size={minimal ? 'sm' : 'md'}
        >
          <Italic className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleUnderline().run()} 
          isActive={editor.isActive('underline')} 
          title="Underline (Ctrl+U)"
          size={minimal ? 'sm' : 'md'}
        >
          <Underline className="w-4 h-4" />
        </ToolbarButton>
        <div className={`w-px bg-slate-200 dark:bg-[#3e4145] ${minimal ? 'h-4 mx-0.5' : 'h-5 mx-1'}`} />
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleBulletList().run()} 
          isActive={editor.isActive('bulletList')} 
          title="Bullet List"
          size={minimal ? 'sm' : 'md'}
        >
          <List className="w-4 h-4" />
        </ToolbarButton>
        <ToolbarButton 
          onClick={() => editor.chain().focus().toggleOrderedList().run()} 
          isActive={editor.isActive('orderedList')} 
          title="Numbered List"
          size={minimal ? 'sm' : 'md'}
        >
          <ListOrdered className="w-4 h-4" />
        </ToolbarButton>
        <div className={`w-px bg-slate-200 dark:bg-[#3e4145] ${minimal ? 'h-4 mx-0.5' : 'h-5 mx-1'}`} />
        <EmojiPickerButton
          title="Insert emoji"
          className={`${minimal ? 'p-1' : 'p-1.5'} rounded transition-colors text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-700 dark:hover:text-slate-200`}
          onEmojiSelect={(emoji) => editor.chain().focus().insertContent(emoji).run()}
        />
        {!minimal && (
          <>
            <div className="w-px h-5 bg-slate-200 dark:bg-[#3e4145] mx-1" />
            <LinkPopover editor={editor} />
          </>
        )}
        </div>
      
      {/* Editor Styles */}
      <style>{`
        .post-editor .ProseMirror {
          outline: none;
          min-height: ${minHeight};
          padding: ${minimal ? '8px 10px' : '12px'};
        }
        .post-editor .ProseMirror h1 {
          font-size: 1.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .post-editor .ProseMirror h2 {
          font-size: 1.25rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .post-editor .ProseMirror h3 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }
        .post-editor .ProseMirror p {
          margin-bottom: 0.5rem;
          line-height: 1.5;
        }
        .post-editor .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .post-editor .ProseMirror ul,
        .post-editor .ProseMirror ol {
          padding-left: 1.25rem;
          margin-bottom: 0.5rem;
        }
        .post-editor .ProseMirror ul {
          list-style-type: disc;
        }
        .post-editor .ProseMirror ol {
          list-style-type: decimal;
        }
        .post-editor .ProseMirror li {
          margin-bottom: 0.25rem;
        }
        /* Match the feed's wrapping rules so the composer shows what the post will look like */
        .post-editor .ProseMirror { overflow-wrap: anywhere; word-break: break-word; min-width: 0; max-width: 100%; }
        .post-editor .ProseMirror a {
          color: #4f46e5;
          text-decoration: underline;
          cursor: text;
        }
        .dark .post-editor .ProseMirror a {
          color: #818cf8;
        }
        .post-editor .ProseMirror p.is-editor-empty:first-child::before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          float: left;
          height: 0;
        }
      `}</style>
      
      {/* Editor Content */}
        <div className="post-editor">
          <EditorContent editor={editor} />
        </div>
      </div>
      {/* @-mention popover — sibling of clip wrapper, anchored to outer relative parent
          so it can float above the entire editor without being cropped by overflow-hidden.
          Renders immediately on @ trigger; shows a loading state if data hasn't arrived yet.
          Loading state requires onMentionTrigger to be wired — without it, the parent
          isn't expected to fetch data, so we suppress the perpetual loading UI. */}
      {mentionQuery !== null && (
        <MentionSuggestions
          ref={mentionSuggestionsRef}
          query={mentionQuery}
          members={members}
          groups={groups}
          loading={!!onMentionTrigger && members === null && groups === null}
          onSelect={handleMentionSelect}
          currentUserId={currentUserId}
        />
      )}
    </div>
  );
}
